/**
 * Copyright (c) 2013 ESHA Research
 * Dual licensed under the MIT and GPL licenses:
 *   http://www.opensource.org/licenses/mit-license.php
 *   http://www.gnu.org/licenses/gpl.html
 *
 * Allows use of the 'overwrite' param on set calls to give an enforced expiration date
 * without breaking existing 'overwrite' functionality.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { StoreAPI } from "store2";

declare module "store2" {
	export function setExpire(key: string, data: any, expiry: Date | number): any;

	export interface StoreAPI {
		setExpire(key: string, data: any, expiry: Date | number): any;
		expire(key: string, expiry: Date | number): boolean;
		removeItem(key: string): any;
	}
}

export default function (store: any) {
	const { _ } = store;

	const $parse = _.parse,
		$get = _.get,
		$has = store.has;

	function getExpiryTimestamp(expiry: Date | number): number {
		return expiry instanceof Date ? expiry.getTime() : expiry;
	}

	function checkExpiration(area: StoreAPI, key: string): void {
		const expiry = /^@exp(\d+);.*$/.exec($get(area, key));
		if (expiry && Date.now() >= Number.parseInt(expiry[1], 10)) area.removeItem(key);
	}

	_.parse = function (str: string, fn: (val: any) => any): string {
		const value = /^@exp\d+;(.*)$/.exec(str)?.[1] ?? str;
		return $parse(value, fn);
	};

	_.get = function (area: StoreAPI, key: string): any {
		checkExpiration(area, key);
		return $get(area, key);
	};

	store.setExpire = _.storeAPI.setExpire = function (key: string, data: any, expiry: Date | number): any {
		return _.set(this._area, this._in(key), `@exp${getExpiryTimestamp(expiry)};${_.stringify(data)}`);
	};

	store.expire = _.storeAPI.expire = function (key: string, expiry: Date | number): boolean {
		if (this.has(key)) {
			store.setExpire(key, this.get(key), expiry);
			return true;
		}
		return false;
	};

	store.has = _.storeAPI.has = function (key: string) {
		checkExpiration(this._area, this._in(key));
		// eslint-disable-next-line prefer-rest-params
		return $has.apply(this, arguments);
	};

	return store;
}
